// src/features/tidsregistering/components/absence-planner/AbsenceRow.jsx
import React, { memo } from 'react';
import { Box, alpha } from '@mui/material';
import { getCategoryHeight, getDateIndex, TASK_HEIGHT, CATEGORY_PADDING } from '../../../../components/timeline';
import { calculateTaskPositions } from '../../../../components/gantt/utils/ganttUtils';
import AbsenceBar from './AbsenceBar';
import { useDragCreate } from './useDragCreate';

/**
 * Renders one staff-member row for the absence planner.
 * Drop-in replacement for CategoryRow — receives the same props injected
 * by the renderRows render-prop in TimelineGrid.
 *
 * Drag to create: press and drag on the row background to select a date range,
 * which fires onDragCreate({ category, startDate, endDate }).
 */
function AbsenceRow({
  category,
  visibleDayIndices,
  getDateForDayIndex,
  dayWidth,
  baseDate,
  onTaskClick,
  onDragCreate,
  unavailabilityBlocks = [],
}) {
  const height = getCategoryHeight(category.tasks);

  // Use the same overlap-aware level calculation that getCategoryHeight uses,
  // so bar positions always match the row height.
  const positionedSlots = calculateTaskPositions(category.tasks, baseDate);
  const levelBySlotId = new Map(positionedSlots.map(({ task, level }) => [task.id, level]));

  const { rowRef, isDragging, ghostBar, handleMouseDown } = useDragCreate({
    visibleDayIndices,
    dayWidth,
    getDateForDayIndex,
    onDragCreate: onDragCreate
      ? ({ startDate, endDate }) => onDragCreate({ category, startDate, endDate })
      : null,
  });

  const firstVisible = visibleDayIndices[0];
  const lastVisible = visibleDayIndices[visibleDayIndices.length - 1];

  const overlays = unavailabilityBlocks.flatMap((block, i) => {
    const startIndex = getDateIndex(new Date(block.start_date), baseDate);
    const endIndex = getDateIndex(new Date(block.end_date), baseDate);
    if (endIndex < firstVisible || startIndex > lastVisible) return [];
    const clampedStart = Math.max(startIndex, firstVisible);
    const clampedEnd = Math.min(endIndex, lastVisible);
    return [{
      key: `unavail-${block.source_id}-${i}`,
      left: (clampedStart - firstVisible) * dayWidth,
      width: (clampedEnd - clampedStart + 1) * dayWidth,
    }];
  });

  return (
    <Box
      ref={rowRef}
      onMouseDown={handleMouseDown}
      sx={{
        position: 'relative',
        height,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: isDragging ? 'col-resize' : 'crosshair',
        userSelect: 'none',
      }}
    >
      {overlays.map(({ key, left, width }) => (
        <Box
          key={key}
          sx={{
            position: 'absolute',
            left,
            top: 0,
            width,
            height: '100%',
            background: 'repeating-linear-gradient(-45deg, rgba(0,0,0,0.14) 0px, rgba(0,0,0,0.14) 2px, rgba(0,0,0,0.04) 2px, rgba(0,0,0,0.04) 9px)',
            pointerEvents: 'none',
          }}
        />
      ))}

      {category.tasks.map((slot) => (
        <AbsenceBar
          key={slot.id}
          slot={slot}
          visibleDayIndices={visibleDayIndices}
          dayWidth={dayWidth}
          baseDate={baseDate}
          level={levelBySlotId.get(slot.id) ?? 0}
          onClick={onTaskClick}
        />
      ))}

      {/* Ghost bar shown while the user is dragging to select a range */}
      {ghostBar && (
        <Box
          sx={{
            position: 'absolute',
            left: ghostBar.leftPx,
            top: CATEGORY_PADDING,
            width: Math.max(ghostBar.widthPx, 2),
            height: TASK_HEIGHT,
            bgcolor: alpha('#2196f3', 0.2),
            border: '1.5px dashed #2196f3',
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      )}
    </Box>
  );
}

export default memo(AbsenceRow);
